import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '@/components/Sidebar';
import Header from '@/components/Header';
import { useTheme } from '@/contexts/ThemeContext';
import { Calendar, CreditCard, DollarSign, AlertCircle, AlertTriangle, CheckCircle, Clock, Edit2, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import '@/styles/commandDeck.css';

const UpcomingPayments = ({ onLogout }) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');

  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showUpdateCard, setShowUpdateCard] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);

  useEffect(() => {
    fetchUpcomingPayments();
    fetchPaymentMethods();
  }, []);

  const fetchUpcomingPayments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/upcoming`,
        { params: { user_email: user.email } }
      );

      if (response.data.success) {
        setPayments(response.data.payments || []);
      }
    } catch (error) {
      console.error('Failed to fetch upcoming payments:', error);
      toast.error('Failed to load upcoming payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/methods`,
        { params: { user_email: user.email } }
      );

      if (response.data.payment_methods) {
        setPaymentMethods(response.data.payment_methods);
      }
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    if (!selectedMethod) {
      toast.error('Please select a payment method');
      return;
    }

    if (!selectedPayment || !selectedPayment.invoice_id) {
      toast.error('Invalid payment selected');
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/update-method`,
        {
          invoice_id: selectedPayment.invoice_id,
          payment_method_id: selectedMethod,
          user_email: user.email
        }
      );

      if (response.data.success) {
        toast.success('Payment method updated successfully');
        setShowUpdateCard(false);
        setSelectedPayment(null);
        setSelectedMethod(null);
        fetchUpcomingPayments();
      }
    } catch (error) {
      console.error('Failed to update payment method:', error);
      toast.error('Failed to update payment method');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const getDaysUntil = (dateString) => {
    if (!dateString) return null;
    const targetDate = new Date(dateString);
    const today = new Date();
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // --- shared inline style helpers -----------------------------------------
  const labelStyle = { fontSize: 11, color: 'rgba(255,255,255,.45)' };
  const valueStyle = { fontSize: 13, fontWeight: 600, color: '#fff', marginTop: 2 };

  return (
    <div className="min-h-screen" style={{ background: '#0A0E1A' }}>
      <Sidebar onLogout={onLogout} />
      <Header onLogout={onLogout} />

      <div className="cd-root sidebar-content pt-16 lg:pt-20">
        {/* Animated bloom blobs */}
        <div className="cd-bloom-wrap">
          <div className="cd-bloom cd-bloom-1" />
          <div className="cd-bloom cd-bloom-2" />
        </div>

        <div className="cd-main" style={{ maxWidth: 980 }}>
          {/* Page Header */}
          <div style={{ marginBottom: 22 }}>
            <div className="cd-display" style={{ fontWeight: 700, fontSize: 11, letterSpacing: 2, color: '#FCD34D' }}>
              SCHEDULED
            </div>
            <h1 className="cd-display" style={{ fontWeight: 800, fontSize: 32, letterSpacing: -1, color: '#fff', marginTop: 8 }}>
              Upcoming payments
            </h1>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,.55)', marginTop: 5 }}>
              Payments we&rsquo;ll charge for jobs you&rsquo;ve booked. Keep your card current.
            </p>
          </div>

          {/* Payments List */}
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="cd-skel"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)',
                    borderRadius: 16, padding: '18px 20px', animation: 'szshimmer 1.4s ease-in-out infinite'
                  }}
                >
                  <span style={{ width: 46, height: 46, borderRadius: 12, background: 'rgba(255,255,255,.08)', flex: '0 0 auto' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ width: 120, height: 11, borderRadius: 6, background: 'rgba(255,255,255,.08)', marginBottom: 10 }} />
                    <div style={{ width: '55%', height: 16, borderRadius: 6, background: 'rgba(255,255,255,.1)', marginBottom: 10 }} />
                    <div style={{ width: '38%', height: 10, borderRadius: 6, background: 'rgba(255,255,255,.07)' }} />
                  </div>
                  <div style={{ width: 80, height: 36, borderRadius: 10, background: 'rgba(255,255,255,.08)', flex: '0 0 auto' }} />
                </div>
              ))}
            </div>
          ) : payments.length === 0 ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
              padding: '60px 20px', background: 'rgba(255,255,255,.025)',
              border: '1px solid rgba(255,255,255,.08)', borderRadius: 18
            }}>
              <span style={{
                width: 64, height: 64, borderRadius: 18, background: 'rgba(40,192,208,.12)', color: '#28C0D0',
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18
              }}>
                <Calendar className="w-8 h-8" />
              </span>
              <div className="cd-display" style={{ fontWeight: 700, fontSize: 19, color: '#fff' }}>
                No scheduled payments
              </div>
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.55)', marginTop: 7, maxWidth: 340, lineHeight: 1.55 }}>
                Payments for jobs you&rsquo;ve booked will appear here. All your payments are up to date.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {payments.map((payment) => {
                const daysUntil = getDaysUntil(payment.payment_date);
                const isPast = daysUntil !== null && daysUntil < 0;
                const isUrgent = daysUntil !== null && daysUntil <= 7 && !isPast;

                // theme by status
                const cardBg = isPast ? 'rgba(185,28,28,.06)' : 'rgba(255,255,255,.045)';
                const cardBorder = isPast
                  ? '1px solid rgba(185,28,28,.35)'
                  : isUrgent ? '1px solid rgba(180,83,9,.3)' : '1px solid rgba(255,255,255,.09)';
                const iconBg = isPast ? 'rgba(185,28,28,.2)' : isUrgent ? 'rgba(180,83,9,.18)' : 'rgba(37,99,235,.16)';
                const iconColor = isPast ? '#F87171' : isUrgent ? '#FCD34D' : '#60A5FA';
                const badgeColor = isPast ? '#F87171' : isUrgent ? '#FCD34D' : '#60A5FA';
                const badgeBg = isPast ? 'rgba(185,28,28,.2)' : isUrgent ? 'rgba(180,83,9,.2)' : 'rgba(37,99,235,.18)';
                const StatusIcon = isPast ? AlertTriangle : isUrgent ? Clock : CreditCard;

                return (
                  <div
                    key={payment.invoice_id}
                    style={{ background: cardBg, border: cardBorder, borderRadius: 16, padding: '20px 22px' }}
                  >
                    <div className="cd-row" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 18 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <span style={{
                          width: 46, height: 46, borderRadius: 12, background: iconBg, color: iconColor,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto'
                        }}>
                          <StatusIcon className="w-6 h-6" />
                        </span>
                        <div>
                          <div className="cd-display" style={{ fontWeight: 700, fontSize: 17, color: '#fff' }}>
                            {payment.job_title}
                          </div>
                          <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.5)', marginTop: 3 }}>
                            <span className="cd-mono">INV-{payment.invoice_number}</span>
                            {payment.payment_type && <> &middot; {payment.payment_type}</>}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div className="cd-display" style={{ fontWeight: 800, fontSize: 24, color: '#fff', letterSpacing: -0.4, lineHeight: 1.1 }}>
                          {formatCurrency(payment.amount)}
                        </div>
                        {daysUntil !== null && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700,
                            color: badgeColor, background: badgeBg, padding: '3px 9px', borderRadius: 999, marginTop: 4
                          }}>
                            {isPast ? `${Math.abs(daysUntil)} days overdue` : `In ${daysUntil} days`}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18,
                      marginTop: 18, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,.07)', flexWrap: 'wrap'
                    }}>
                      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
                        <div>
                          <div style={labelStyle}>Scheduled</div>
                          <div style={{ ...valueStyle, color: isPast ? '#F87171' : '#fff' }}>
                            {formatDate(payment.payment_date)}
                          </div>
                        </div>
                        <div>
                          <div style={labelStyle}>Job date</div>
                          <div style={valueStyle}>{formatDate(payment.job_date)}</div>
                        </div>
                        <div>
                          <div style={labelStyle}>Charging</div>
                          <div className="cd-mono" style={valueStyle}>
                            {payment.card_brand} &bull;&bull;&bull;&bull; {payment.card_last4}
                            {payment.card_exp_month && payment.card_exp_year && (
                              <> &middot; {payment.card_exp_month}/{payment.card_exp_year}</>
                            )}
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setSelectedPayment(payment);
                          setShowUpdateCard(true);
                        }}
                        className="cd-iconbtn"
                        style={isPast ? {
                          display: 'flex', alignItems: 'center', gap: 8, padding: '11px 18px', borderRadius: 11,
                          background: '#F87171', border: 'none', color: '#0C1120', fontWeight: 700, fontSize: 12.5,
                          cursor: 'pointer', flex: '0 0 auto'
                        } : {
                          display: 'flex', alignItems: 'center', gap: 8, padding: '11px 18px', borderRadius: 11,
                          background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.16)', color: '#fff',
                          fontWeight: 600, fontSize: 12.5, cursor: 'pointer', flex: '0 0 auto'
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                        <span>{isPast ? 'Update card now' : 'Update card'}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Update Payment Method Modal */}
      {showUpdateCard && selectedPayment && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20, background: 'rgba(8,11,20,.66)', backdropFilter: 'blur(5px)',
          fontFamily: "'Work Sans', system-ui, sans-serif"
        }}>
          <div style={{
            width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto', background: '#12162A',
            border: '1px solid rgba(255,255,255,.12)', borderRadius: 22, boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)'
          }}>
            {/* Header */}
            <div style={{ position: 'relative', padding: '24px 26px 18px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
              <button
                onClick={() => {
                  setShowUpdateCard(false);
                  setSelectedMethod(null);
                }}
                style={{
                  position: 'absolute', top: 16, right: 16, width: 34, height: 34, borderRadius: 10,
                  background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.18)', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 3
                }}
              >
                <X className="w-4 h-4" />
              </button>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 20, color: '#fff', letterSpacing: -0.4 }}>
                Update payment method
              </div>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.55)', marginTop: 5 }}>
                Payment for <span style={{ color: '#fff' }}>{selectedPayment.job_title}</span>
                {' '}&middot; {formatCurrency(selectedPayment.amount)} scheduled {formatDate(selectedPayment.payment_date)}
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '20px 26px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'rgba(255,255,255,.4)', marginBottom: 9 }}>
                CHARGE THIS CARD
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                {paymentMethods.length === 0 ? (
                  <p style={{ color: 'rgba(255,255,255,.6)', textAlign: 'center', padding: '16px 0', fontSize: 13.5 }}>
                    No payment methods found. Please add a card in Settings.
                  </p>
                ) : (
                  paymentMethods.map((method) => {
                    const isSelected = selectedMethod === method.id;
                    return (
                      <label
                        key={method.id}
                        onClick={() => setSelectedMethod(method.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12, padding: '13px 15px', borderRadius: 12,
                          cursor: 'pointer',
                          border: isSelected ? '1px solid #28C0D0' : '1px solid rgba(255,255,255,.12)',
                          background: isSelected ? 'rgba(40,192,208,.08)' : 'transparent'
                        }}
                      >
                        <span style={{
                          width: 18, height: 18, borderRadius: 999, flex: '0 0 auto',
                          border: isSelected ? '5px solid #28C0D0' : '2px solid rgba(255,255,255,.3)',
                          background: isSelected ? '#fff' : 'transparent'
                        }} />
                        <CreditCard className="w-5 h-5" style={{ color: 'rgba(255,255,255,.7)', flex: '0 0 auto' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13.5, color: '#fff', fontWeight: 600 }}>
                            {method.card.brand} &bull;&bull;&bull;&bull; {method.card.last4}
                          </div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,.5)' }}>
                            Exp {method.card.exp_month}/{method.card.exp_year}
                          </div>
                        </div>
                        {isSelected && <CheckCircle className="w-5 h-5" style={{ color: '#28C0D0', flex: '0 0 auto' }} />}
                      </label>
                    );
                  })
                )}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => {
                    setShowUpdateCard(false);
                    setSelectedMethod(null);
                  }}
                  style={{
                    flex: 1, padding: 13, borderRadius: 12, background: 'transparent',
                    border: '1px solid rgba(255,255,255,.2)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdatePaymentMethod}
                  disabled={!selectedMethod}
                  style={{
                    flex: 1.6, padding: 13, borderRadius: 12, background: '#28C0D0', border: 'none',
                    color: '#0C1120', fontWeight: 700, fontSize: 14, cursor: selectedMethod ? 'pointer' : 'not-allowed',
                    opacity: selectedMethod ? 1 : 0.5
                  }}
                >
                  Update payment method
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpcomingPayments;
